
import LineChart from "@rio-cloud/rio-uikit/LineChart";
import Line from "@rio-cloud/rio-uikit/Line";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import { OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../../../../enums/occurrenceStep";
import { StepChart } from "./stepChart";
import { OccurrenceWithTimeSpent } from "../../../../models/occurrence";

type LineData = {
  date: string;
  qtd: number;
};

export interface QuantityChartProps {
  activeTab: string;
  setActiveTab: (activeTab: string | null) => void;
  contentWithTooltip: (value: number) => any;
  occurrencesByDays: { [key in OccurrenceStepEnum]: { daily: { date: string, occurrences: any[] }[], monthly: { date: string, occurrences: any[] }[] } };
  occurrencesBySteps: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] };
  finalizedOccurrencesBySteps?: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] };
  timePeriod: "daily" | "monthly";
  containerWidth: number;
  adjustDataWithMinHeight: (data: any[], minHeightPercentage: number) => any[];
  isResponsiveView: boolean;
  loading?: boolean;
  chartWidthMultiplier: number;
  defaultChartMinWidth: number;
  dynamicBarSize: number;
  LEFT_LABEL_PAD: number;
  showQuantityChart: boolean;
  setShowQuantityChart: (showQuantityChart: boolean) => void;
  setStepFilter: (stepFilter: OccurrenceStepEnum | null) => void;
  occurrenceStepAvailable: string[];
  assetIds?: string[];
  selectedMetric: "average" | "quantity";
  setTimePeriod: (timePeriod: "daily" | "monthly") => void;
  finishedFilter: string[];
}

export const QuantityChart = (props: QuantityChartProps) => {
  return (
    <>
      <span className="flex w-full justify-between text-size-16 font-semibold p-8">
        <div>
          Quantidade por etapa de reparo - {props.timePeriod === "daily" ? "Diário" : "Mensal"}
        </div>
        <div
          className="cursor-pointer"
          onClick={() => props.setShowQuantityChart(!props.showQuantityChart)}
        >
          <span
            className={`rioglyph rioglyph-chevron-${props.showQuantityChart ? "up" : "down"
              }`}
          />
        </div>
      </span>

      {props.showQuantityChart &&
        !(props.finishedFilter.length === 1 && props.finishedFilter.includes('true')) &&
        (
          <div className="flex items-center overflow-x-auto flex-nowrap pb-2 mb-4 -mx-4 px-4 snap-x snap-mandatory gap-3">
            {props.occurrenceStepAvailable.map((stepEnum) => {
              const value = [...new Set(props.assetIds ?? [])].length > 0 ? props.occurrencesByDays[OccurrenceStepEnum[stepEnum as keyof typeof OccurrenceStepEnum]]?.[props.timePeriod]?.reduce((acc, curr) => acc + curr.occurrences.length, 0) ?? 0 : 0;
              const stepKey = OccurrenceStepEnum[stepEnum as keyof typeof OccurrenceStepEnum];
              return (
                <div
                  key={stepEnum}
                  onClick={() => {
                    if (value === 0) return;
                    if (props.activeTab === stepKey) {
                      props.setActiveTab(null);
                      props.setStepFilter(null);
                    } else {
                      props.setActiveTab(stepKey);
                      props.setStepFilter(stepKey);
                    }
                  }}
                  className={`
                      flex flex-col justify-center border-b-2 border-t-2 text-left transition bg-gray-100 h-[65px] px-4 snap-start
                      ${value === 0 ? "cursor-not-allowed" : "cursor-pointer"} 
                      ${props.isResponsiveView ? "flex-none min-w-[150px]" : "flex-1"}  
                      ${props.activeTab === stepKey ? "bg-white text-color-primary font-bold border-b-4 border-b-[#30B4C0]" : "hover:bg-gray-100 text-color-gray"}
                      ${stepEnum !== props.occurrenceStepAvailable[props.occurrenceStepAvailable.length - 1] ? "border-r-[1px]" : ""}
                    `}
                >
                  <div className="text-size-12 line-clamp-2 h-[25px]">
                    {OccurrenceStepLabelEnum[stepEnum as keyof typeof OccurrenceStepLabelEnum]}
                  </div>
                  <div className="font-semibold text-size-16 truncate mt-2">
                    {props.contentWithTooltip(value)}
                  </div>
                </div>
              )
            })}
          </div>
        )}

      {props.showQuantityChart && props.activeTab === '' && (
        <div className="w-full h-full overflow-x-auto px-8 py-4">
          <StepChart
            adjustDataWithMinHeight={props.adjustDataWithMinHeight}
            data={props.occurrencesBySteps}
            finalizedData={props.finalizedOccurrencesBySteps}
            chartWidthMultiplier={props.chartWidthMultiplier}
            defaultChartMinWidth={props.defaultChartMinWidth}
            dynamicBarSize={props.dynamicBarSize}
            isResponsiveView={props.isResponsiveView}
            loading={props.loading}
            metric="quantity"
          />
        </div>
      )}

      {props.showQuantityChart && props.activeTab !== '' && (
        <div className="w-full px-4 sm:px-20 py-4 overflow-x-auto">
          {props.loading === true ? (
            <div className="flex justify-center items-center h-[200px]">
              <Spinner show text="Carregando gráfico..." />
            </div>
          ) : (
            <div
              style={{
                minWidth: "100%",
                width: props.isResponsiveView
                  ? `${Math.max(
                    (props.occurrencesByDays[props.activeTab as keyof typeof props.occurrencesByDays]?.[props.timePeriod]?.length || 0) * (props.isResponsiveView ? 160 : 130),
                    500,
                  )}px`
                  : `${props.containerWidth}px`,
                height: 200,
              }}
            >
              <LineChart
                data={[
                  {
                    date: '',
                    qtd: 0,
                  },
                  ...(props.occurrencesByDays[props.activeTab as keyof typeof props.occurrencesByDays]?.[props.timePeriod] ?? [])
                    .map((item, index) => {
                      return {
                        date: (() => {
                          if (props.timePeriod === "monthly" && item.date.match(/^\d{4}-\d{2}$/)) {
                            const [year, month] = item.date.split("-");
                            const monthNames = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
                            return `${monthNames[parseInt(month, 10) - 1]}/${year}`;
                          }

                          return new Date(`${item.date}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                        })(),
                        qtd: (props.occurrencesByDays[props.activeTab as keyof typeof props.occurrencesByDays]?.[props.timePeriod] ?? [])?.slice(0, index + 1).reduce((acc, curr) => acc + curr.occurrences.length, 0),
                      };
                    })
                ].filter((item, index) => item.qtd > 0 || index === 0)}
                dataKey={(entry: LineData) => entry.date}
                xAxisOptions={{
                  type: "category",
                  interval: props.isResponsiveView && props.timePeriod === "daily" ? 1 : 0,
                  angle: -45,
                  textAnchor: "end",
                  height: 60,
                  tickMargin: 8,
                  padding: { left: props.LEFT_LABEL_PAD, right: 30 },
                  tick: {
                    angle: props.isResponsiveView ? -45 : -25,
                    textAnchor: props.isResponsiveView ? 'end' : 'middle',
                    dy: props.isResponsiveView ? 0 : 3
                  } as any
                }}
                margin={{ left: -5, right: 30, top: 25, bottom: 50 }}
                showGrid
                tooltip={false}
                gridOptions={{
                  vertical: false,
                  horizontal: false,
                  strokeDasharray: "0",
                  stroke: "gray-lighter",
                }}
                style={{ color: "#a7afbb", fontSize: 12 }}
                lines={[
                  <Line
                    key="line"
                    dataKey="qtd"
                    type="linear"
                    strokeWidth={2}
                    isAnimationActive={false}
                    label={{
                      offset: props.isResponsiveView ? 8 : 10,
                      position: "top",
                      fill: "#a7afbb",
                      fontSize: props.isResponsiveView ? 12 : 14,
                      formatter: (v: number) => (v > 0 ? `${v}` : ""),
                    }}
                    strokeColor="color-highlight"
                  />,
                ]}
              />
            </div>
          )}
        </div>
      )}

      {props.selectedMetric === "quantity" && props.activeTab && (
        <div className="w-[200px] mb-4">
          <button
            className={`btn rounded-tr-none rounded-br-none w-1/2 ${props.timePeriod === "monthly" ? "btn-primary" : "btn-default"}`}
            onClick={() => props.setTimePeriod("monthly")}
          >
            Mensal
          </button>
          <button
            className={`btn rounded-tl-none rounded-bl-none w-1/2 ${props.timePeriod === "daily" ? "btn-primary" : "btn-default"}`}
            onClick={() => props.setTimePeriod("daily")}
          >
            Diário
          </button>
        </div>
      )}
    </>
  );
};
